/**
 * CSP refusal of a string eval made by a script injected into the page, not by
 * our bundle. Browser-automation tools (Puppeteer/Playwright polling a string
 * predicate) and scanners evaluate code as text; our script-src has no
 * 'unsafe-eval', so Chrome refuses and the rejection reaches Sentry's
 * onunhandledrejection. HOK-OS-8F: 41 events from AWS IPs on /login, every
 * frame `<anonymous>` (eval ← predicate ← next) under Sentry's own wrapper.
 *
 * Decided by the frame that made the eval: injected code has no script URL
 * (`<anonymous>`), while an eval from our bundle sits under /assets/*.js. Sentry's
 * wrapper frame is in our bundle too, so "any first-party frame" can't decide it.
 * Without a stack nothing is known, and the event is kept.
 */

interface Frame {
  filename?: string;
  function?: string;
}

interface ExceptionValue {
  type?: string;
  value?: string;
  stacktrace?: { frames?: Frame[] };
}

const CSP_EVAL_REFUSAL = "'unsafe-eval' is not an allowed source of script";

function isAnonymous(frame: Frame): boolean {
  return !frame.filename || frame.filename === '<anonymous>';
}

export function isInjectedEvalError(exception: ExceptionValue): boolean {
  if (exception.type !== 'EvalError') return false;
  if (!exception.value?.includes(CSP_EVAL_REFUSAL)) return false;

  // Sentry orders frames outermost first; drop the eval frame itself.
  const frames = [...(exception.stacktrace?.frames ?? [])];
  if (frames.length > 0 && frames[frames.length - 1].function === 'eval') frames.pop();
  const caller = frames[frames.length - 1];
  return caller !== undefined && isAnonymous(caller);
}
